import { PRODUCT_CONFIG } from "@stb/product-config"
import { createFileRoute } from "@tanstack/react-router"

import { TRIAL_DAYS } from "@/brand/plan-numbers"
import { localizeHref } from "@/core/i18n/locale-context"
import { breadcrumbJsonLd, buildHead } from "@/core/seo"

import { ATO_RECORDS_CHECKED, ATO_RECORDS_URL } from "../../components/home/home-content"
import { AnswerBox, Breadcrumb, CtaBand, SimpleTable, SourceLine } from "../../components/landing"
import { formatDateZh } from "../../content/zh/dates"

/** Chinese version of /how-it-works (D-WEB-LANG-SCOPE); same steps and figures as the English page. */
export const howItWorksZhSeo = () =>
  buildHead({
    title: "使用方法：行程、收据、发票",
    description:
      "安装应用，开车、拍收据。在网页上核对行程，报税时把行程日志导出成 CSV 交给会计师。仅为估算，不是税务建议。",
    path: "/how-it-works",
    localePrefix: "zh",
    jsonLd: [breadcrumbJsonLd([{ name: "首页", path: "/zh" }, { name: "使用方法" }])],
  })

export const Route = createFileRoute("/_brand-layout/zh/how-it-works")({
  head: () => {
    const { meta, links, scripts } = howItWorksZhSeo()
    return { meta, links, scripts }
  },
  component: HowItWorksZhPage,
})

const STEPS = [
  {
    title: "安装并登录",
    body: "用邮箱登录，不用记密码。添加你的车。",
  },
  {
    title: "开车、拍照",
    body: "行程在后台自动记录。一路拍下收据，随手开发票。",
  },
  {
    title: "核对并导出",
    body: "在网页上核对行程并分类。把行程日志下载成 CSV，交给会计师。",
  },
] as const

const PHONE_VS_WEB = [
  {
    header: "行程",
    phone: "用 GPS 在后台记录每一次驾驶。",
    web: "核对行程、补上用途，并一次给多个行程分类。",
  },
  {
    header: "收据",
    phone: "拍下收据。我们读出的内容经你核对后才算数。Logbook 套餐不含此功能。",
    web: "核对我们读出的内容、修改，并在开支里查看收据。",
  },
  {
    header: "发票",
    phone: "开发票，看谁已付款。Logbook 套餐不含此功能。",
    web: "开发票、看谁已付款，并管理客户。",
  },
  {
    header: "报告",
    phone: "手机上暂时没有。",
    web: "把行程日志下载成 CSV，或把全部数据导出成 zip。",
  },
] as const

const linkClass =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
const L = (path: string) => localizeHref(path, "zh")

export function HowItWorksZhPage() {
  const checked = formatDateZh(ATO_RECORDS_CHECKED)
  return (
    <>
      <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "使用方法" }]} />
      <header className="bg-muted/40 px-band py-8 md:py-12">
        <h1 className="text-3xl font-bold tracking-tight md:text-5xl">使用方法</h1>
        <p className="text-muted-foreground mt-3 max-w-[60ch] text-lg">
          手机负责记录，网页用来核对和出报告。
        </p>
        <div className="mt-6 max-w-[70ch]">
          <AnswerBox
            source={
              <SourceLine
                sources={[{ label: "ATO，保存经营记录概览", href: ATO_RECORDS_URL }]}
                checked={checked}
              />
            }
          >
            安装应用，添加你的车，然后开车。行程记在手机上，你在网页上核对。收据随手拍下。报税时把行程日志导出成
            CSV 交给会计师。ATO 要求大多数经营记录保存 5 年，所以每次导出都请自己留一份。
          </AnswerBox>
        </div>
      </header>

      <section className="px-band grid gap-4 py-8" aria-labelledby="steps-title">
        <h2 id="steps-title" className="text-2xl font-semibold">
          三个步骤
        </h2>
        <ol className="grid gap-4 md:grid-cols-3">
          {STEPS.map(({ title, body }, i) => (
            <li key={title} className="flex gap-4">
              <span
                aria-hidden="true"
                className="bg-primary text-primary-foreground grid size-9 shrink-0 place-items-center rounded-full font-bold"
              >
                {i + 1}
              </span>
              <div>
                <h3 className="font-semibold">{title}</h3>
                <p className="text-muted-foreground mt-1">{body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="px-band grid gap-4 py-8" aria-labelledby="split-title">
        <h2 id="split-title" className="text-2xl font-semibold">
          手机记录，网页核对和出报告
        </h2>
        <SimpleTable
          caption="手机和网页：各自负责什么"
          columns={["任务", "在手机上", "在网页上"]}
          rows={PHONE_VS_WEB.map((r) => ({ header: r.header, cells: [r.phone, r.web] }))}
        />
        <p className="max-w-[70ch]">
          每项任务的详细说明：
          <a href={L("/mileage-tracker")} className={linkClass}>
            里程记录
          </a>
          、
          <a href={L("/receipt-scanner")} className={linkClass}>
            收据扫描
          </a>
          和
          <a href={L("/invoicing")} className={linkClass}>
            开发票
          </a>
          。
        </p>
      </section>

      <section className="px-band grid gap-3 py-8" aria-labelledby="data-title">
        <h2 id="data-title" className="text-2xl font-semibold">
          你的数据会怎样
        </h2>
        <p className="max-w-[70ch]">
          行程记在你的手机上，联网时同步到你的账户。收据图片和助手消息，只有在你使用 AI
          读取收据或助手时，才会发送给
          Anthropic（位于澳大利亚境外）。完整的服务清单，以及如何导出或删除数据，见
          <a href={L("/trust")} className={linkClass}>
            你的数据存放在哪里
          </a>
          。其他问题见
          <a href={L("/faq")} className={linkClass}>
            常见问题
          </a>
          。
        </p>
      </section>

      <section className="px-band grid gap-3 py-8" aria-labelledby="free-title">
        <h2 id="free-title" className="text-2xl font-semibold">
          免费开始
        </h2>
        <p className="max-w-[70ch]">
          免费版每月可记录 {PRODUCT_CONFIG.plans.free.tripsMonthly} 个 GPS 行程。新账户还可免费试用
          Logbook 或 Pro {TRIAL_DAYS} 天，无需绑定银行卡。
          {PRODUCT_CONFIG.pricesDraft && "套餐价格在确认之前都是草案。"}
          <a href={L("/pricing")} className={linkClass}>
            查看价格
          </a>
        </p>
      </section>

      <section
        role="note"
        aria-label="Kestly 不做什么"
        className="bg-accent text-accent-foreground mx-band my-4 grid gap-1 rounded-lg px-5 py-4"
      >
        <h2 className="font-semibold">局限</h2>
        <p>
          Kestly 不代你提交 BAS 或报税表，也不提供税务建议。扣除额只是估算，请向你的会计师或 ATO
          核实。
        </p>
      </section>

      <CtaBand
        title="想试试吗？"
        description={`免费试用 ${TRIAL_DAYS} 天，无需绑定银行卡。`}
        ctaLabel="开始免费试用"
        ctaHref="/login"
      />
    </>
  )
}
